import { Component, OnInit, Inject } from '@angular/core';
import { isFunction } from 'lodash-es';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';

import { ViewService } from '../../../../../../../services/view.service';
import { RecordService } from '../../../../../../../services/record.service';
import { NotificationService } from '../../../../../../../services/notification.service';
import { Select } from "./../../../../../../../objects/select";
import { DialogButtonConfig } from "./../../../../../../../objects/dialog-button-config";
import { BehaviorSubject } from 'rxjs';
import { map, filter, finalize } from 'rxjs/operators';
import { data_get, fallback, blank, filled } from '../../../../../../../shared/utils/common';

@Component({
  selector: 'app-annual-asset-report',
  templateUrl: './annual-asset-report.component.html',
  styleUrls: ['./annual-asset-report.component.scss']
})
export class AnnualAssetReportComponent implements OnInit {

  public bFailedAssetsOnly: boolean = false;
  public bIncludeJobChecklists: boolean = false;
  public bIncludeFullAssetChecklists: boolean = false;
  public bCustomField: boolean = false;
  public arSelectedIds: Array<string> = [];
  public bSelectAll: boolean = false;

  /**
   * @type {Select[]}
   */
  public options: Select[] = [];

  /**
   * @type {BehaviorSubject<boolean>}
   */
  readonly loading$ = new BehaviorSubject(true);

  /**
   * @type {DialogButtonConfig[]}
   */
  public arButtons: DialogButtonConfig[] = [
    {
      icon: (this.data.has_custom_field) ? null : ['fas', 'file-pdf'],
      text: (this.data.has_custom_field) ? 'continue' : 'preview',
      callback: 'onSubmit',
      disabled: this.loading$,
    },
    {
      icon: ['fas', 'times'],
      text: 'close',
      callback: 'closeDialog'
    },
  ]

  get title(): string {
    if (data_get(this.data, 'module_name') == 'jobs') {
      return 'job_inspection_report';
    }

    return 'asset_summary_report'
  }

  get shouldShowOptionToIncludeJobChecklists(): boolean {
    return data_get(this.data, 'module_name') == 'jobs';
  }

  get shouldShowOptionToIncludeFullAssetChecklistsReport(): boolean {
    return data_get(this.data, 'module_name') == 'jobs';
  }

  get emptyResults(): boolean {
    return blank(this.options) && data_get(this.data, 'has_job_checklist') === false;
  }

  get hasResults(): boolean {
    return filled(this.options) || data_get(this.data, 'has_job_checklist') === true;
  }

  get hasOnlyJobChecklists(): boolean {
    return data_get(this.data, 'has_job_checklist') === true && blank(this.options);
  }

  constructor(
    private viewService: ViewService,
    private recordService: RecordService,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private notificationService: NotificationService,
    private dialogRef: MatDialogRef<AnnualAssetReportComponent>
  ) { }

  ngOnInit() {
    this.getAssetType(this.data.id, fallback(data_get(this.data, 'module_name'), {
      fallback: () => 'sites',
    }))

    if (this.hasOnlyJobChecklists) {
      this.bIncludeJobChecklists = true;
    }
  }

  /**
   * get asset type data
   */
  getAssetType(id: string, moduleName: string): void {
    this.recordService.getAssetUniqueAssetTypes('assets', {
      ... (moduleName === 'sites' && {
        site_id: id,
      }),
      ... (moduleName === 'jobs' && {
        job_id: id,
      })
    }).pipe(
      finalize(() => this.loading$.next(false)),
      map((objData) => objData.body.map(objAsset => {
        return new Select(objAsset['id'], objAsset['text'], '', null, true);
      })),
      filter((arOptions) => arOptions.length)
    ).subscribe(arOptions => {
      this.bSelectAll = true;
      this.arSelectedIds = arOptions.map((option) => option.id);
      this.options = arOptions;
      this.bIncludeJobChecklists = this.hasOnlyJobChecklists;
    });
  }

  /**
   * select option and store in filter
   *
   * @param event MatCheckboxChange
   * @param {Select} record set of dropdown option
   * @param {boolean} selectAll determine we need to select all data
   */
  markAsSelected(event, record: Select, markAll: boolean = false ): void {
    if (event.checked) {
      if (markAll) {
        this.options.forEach( option => {
          option.selected = true;
          this.arSelectedIds.push(option.id);
        });
      } else {
        this.arSelectedIds.push(record.id);
      }
    } else {
      this.bSelectAll = false;
      if (markAll) {
        this.options.forEach( option => {
          option.selected = false;
        });
        this.arSelectedIds = [];
      } else {
        this.arSelectedIds = this.arSelectedIds.filter( id => id != record.id);
      }
    }
  }

  /**
   * close the dialog
   *
   * @returns {void}
   */
  closeDialog(data: object = null): void {
    this.dialogRef.close(data);
  }

  /**
   * submit dialog
   *
   * @returns {void}
   */
  onSubmit(): void {
    if (this.hasOnlyJobChecklists) {
      if (this.bIncludeJobChecklists) {
        this._previewReport();
      } else {
        this.notificationService.notifyWarning('please_select_include_job_checklists');
      }

      return;
    }

    if (this.arSelectedIds.length) {
      this._previewReport();
    } else {
      this.notificationService.notifyWarning('please_select_asset_type');
    }
  }

  /**
   * Wait until the child component emits the data
   *
   * @param event
   *
   * @returns {void}
   */
  ngOnHeaderChange(event): void {
    if (isFunction(this[event.callback])) {
        this[event.callback]();
    } else {
        this.closeDialog();
    }
  }

  private _previewReport(): void {
    this.closeDialog({
      asset_type_id: this.arSelectedIds,
      customer_id: this.viewService.arRecord.customer_id || null,
      is_failed: this.bFailedAssetsOnly,
      include_job_checklists: this.bIncludeJobChecklists,
      include_full_assets_checklists: this.bIncludeFullAssetChecklists,
    });
  }
}
